import * as THREE from 'three';
import { bboxOfPoly, distToSegment2, pointInPoly, polyArea } from '../../shared/geom.js';
import type { Region } from '../../shared/region.js';
import { hashUnit } from '../../shared/rng.js';
import type { Building, World } from '../../shared/types.js';

/**
 * What stands on the roofs: chimney stacks across Europe, with Paris's terracotta pots on
 * them, and on a Manhattan walk-up the cedar water tank on its legs.
 *
 * It is the skyline, more than anything at street level, that says which city this is — a
 * flat roofline is a model, and the same flat roofline in Kraków and New York is the same
 * model twice. Slop New York puts a water tower on a quarter of its masonry buildings over
 * fourteen metres, and that is the rule here too, for the same reason: it is what the city
 * looks like.
 *
 * Instanced, one draw call per kind, and only on flat roofs — a stack on a hip roof would
 * need the roof's height under it, and the hip roofs are houses that mostly have no
 * chimney anyone can see from a street. Every placement is a hash of the building id, so
 * the same roofs have the same chimneys for everyone. The count is capped by what is left of
 * the triangle budget once the city itself is built: in the Marais that is not much, and
 * the buildings nearest the route get theirs first.
 */

/** Chimney stacks per eligible roof, at most, and how far in from the roof edge. */
const STACKS_MAX = 3;
const STACK_INSET = 0.8;
const WATER_TOWER_CHANCE = 0.25;
const WATER_TOWER_MIN_HEIGHT = 14;
const WATER_TOWER_MAX_HEIGHT = 70;
const WATER_TOWER_MIN_AREA = 150;
/**
 * Never more than this, whatever the budget allows. Seen from a chase camera a chimney is a
 * few pixels against the sky; eighty thousand triangles of them in Kraków was most of what
 * the region had left, for a skyline only the aerial camera ever really sees.
 */
const MAX_TRIANGLES = 40_000;

export interface Rooftops {
  objects: THREE.Object3D[];
  triangles: number;
  dispose(): void;
}

export function buildRooftops(
  world: World,
  region: Region,
  baseOf: (b: Building) => number,
  /** Triangles this may spend. */
  budget: number,
): Rooftops {
  const objects: THREE.Object3D[] = [];
  const disposables: { dispose(): void }[] = [];
  let triangles = 0;
  budget = Math.min(budget, MAX_TRIANGLES);
  if (budget <= 0) return { objects, triangles, dispose() {} };

  const paris = region === 'fr';
  const stack = paris ? parisStack() : plainStack();
  const tower = waterTower();
  const stackTris = stack.index ? stack.index.count / 3 : stack.getAttribute('position').count / 3;
  const towerTris = tower.index ? tower.index.count / 3 : tower.getAttribute('position').count / 3;

  // Nearest the route first, so a tight budget is spent where the race actually goes.
  const route = world.route.path;
  const order = world.buildings
    .filter((b) => b.roofStyle !== 'pitched' && b.height > 5)
    .map((b) => ({ b, d: routeDistance(route, b.poly) }))
    .sort((p, q) => p.d - q.d || p.b.id - q.b.id);

  const stackPlacements: { m: THREE.Matrix4; color: THREE.Color }[] = [];
  const towerPlacements: THREE.Matrix4[] = [];
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  let spent = 0;

  for (const { b } of order) {
    const look = hashUnit(world.seed, b.id ^ 0x1b873593);
    const top = baseOf(b) + b.height - b.minHeight;
    const bb = bboxOfPoly(b.poly);
    const area = Math.abs(polyArea(b.poly));

    const towerHere =
      region === 'us' &&
      (b.material === 'brick' || b.material === 'stone') &&
      b.height >= WATER_TOWER_MIN_HEIGHT &&
      b.height <= WATER_TOWER_MAX_HEIGHT &&
      area >= WATER_TOWER_MIN_AREA &&
      look < WATER_TOWER_CHANCE;
    if (towerHere && spent + towerTris <= budget) {
      const spot = pointInside(b.poly, bb, world.seed, b.id, 3.5);
      if (spot) {
        const s = 0.85 + ((look * 7.7) % 1) * 0.35;
        q.setFromAxisAngle(up, ((look * 3.3) % 1) * Math.PI);
        towerPlacements.push(m.clone().compose(new THREE.Vector3(spot.x, top, spot.z), q, new THREE.Vector3(s, s, s)));
        spent += towerTris;
      }
    }

    // Chimneys belong to the old buildings, where there was a fire in every room.
    const old = b.style === 'period' || b.style === 'ornate' || b.style === 'vernacular';
    if (!old || region === 'us') continue;
    const stacks = 1 + Math.floor(((look * 11.3) % 1) * STACKS_MAX * Math.min(1, area / 250));
    const colour = new THREE.Color().setHex(b.material === 'brick' ? 0x7f4434 : b.color, THREE.SRGBColorSpace);
    for (let k = 0; k < stacks; k++) {
      if (spent + stackTris > budget) break;
      const spot = pointInside(b.poly, bb, world.seed, b.id * 7 + k, STACK_INSET);
      if (!spot) continue;
      const tall = 1.1 + hashUnit(world.seed, b.id * 31 + k) * 1.1;
      q.setFromAxisAngle(up, spot.angle);
      stackPlacements.push({
        m: m.clone().compose(new THREE.Vector3(spot.x, top, spot.z), q, new THREE.Vector3(1, tall, 1)),
        color: colour,
      });
      spent += stackTris;
    }
  }

  if (stackPlacements.length) {
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true });
    const mesh = new THREE.InstancedMesh(stack, material, stackPlacements.length);
    stackPlacements.forEach((p, i) => {
      mesh.setMatrixAt(i, p.m);
      mesh.setColorAt(i, p.color);
    });
    finish(mesh, 'chimneys');
    objects.push(mesh);
    disposables.push(stack, material);
    triangles += stackPlacements.length * stackTris;
  } else {
    stack.dispose();
  }
  if (towerPlacements.length) {
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    const mesh = new THREE.InstancedMesh(tower, material, towerPlacements.length);
    towerPlacements.forEach((p, i) => mesh.setMatrixAt(i, p));
    finish(mesh, 'water-towers');
    objects.push(mesh);
    disposables.push(tower, material);
    triangles += towerPlacements.length * towerTris;
  } else {
    tower.dispose();
  }

  return {
    objects,
    triangles,
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

function finish(mesh: THREE.InstancedMesh, name: string): void {
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
}

/**
 * A point on the roof far enough in from every edge, by a few hashed tries — and the angle of
 * the nearest edge, so a stack stands square to the wall it is built into.
 */
function pointInside(
  poly: number[],
  bb: { minX: number; minZ: number; maxX: number; maxZ: number },
  seed: number,
  key: number,
  inset: number,
): { x: number; z: number; angle: number } | null {
  const n = poly.length / 2;
  for (let attempt = 0; attempt < 6; attempt++) {
    const x = bb.minX + hashUnit(seed, key * 13 + attempt * 2) * (bb.maxX - bb.minX);
    const z = bb.minZ + hashUnit(seed, key * 13 + attempt * 2 + 1) * (bb.maxZ - bb.minZ);
    if (!pointInPoly(poly, x, z)) continue;
    let nearest = Infinity;
    let angle = 0;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const d2 = distToSegment2(x, z, poly[i * 2], poly[i * 2 + 1], poly[j * 2], poly[j * 2 + 1]).d2;
      if (d2 < nearest) {
        nearest = d2;
        angle = -Math.atan2(poly[j * 2 + 1] - poly[i * 2 + 1], poly[j * 2] - poly[i * 2]);
      }
    }
    if (nearest >= inset * inset) return { x, z, angle };
  }
  return null;
}

function routeDistance(path: number[], poly: number[]): number {
  const x = poly[0];
  const z = poly[1];
  let best = Infinity;
  for (let i = 0; i + 3 < path.length; i += 2) {
    best = Math.min(best, distToSegment2(x, z, path[i], path[i + 1], path[i + 2], path[i + 3]).d2);
  }
  return best;
}

/** Vertex-coloured geometry with every face flat, merged from boxes. */
function boxes(parts: { w: number; h: number; d: number; x: number; y: number; z: number; color: number }[]): THREE.BufferGeometry {
  const merged: THREE.BufferGeometry[] = [];
  const c = new THREE.Color();
  for (const p of parts) {
    // Without its underside, which sits on the roof and is never seen: a sixth of the cost.
    const g = withoutBottom(new THREE.BoxGeometry(p.w, p.h, p.d));
    g.translate(p.x, p.y + p.h / 2, p.z);
    c.setHex(p.color, THREE.SRGBColorSpace);
    const colors = new Float32Array(g.getAttribute('position').count * 3);
    for (let i = 0; i < colors.length; i += 3) {
      colors[i] = c.r;
      colors[i + 1] = c.g;
      colors[i + 2] = c.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    g.deleteAttribute('uv');
    merged.push(g);
  }
  const out = mergeNonIndexed(merged);
  for (const g of merged) g.dispose();
  return out;
}

/** A box's -Y face is its fourth group; dropped, the box is ten triangles rather than twelve. */
function withoutBottom(box: THREE.BoxGeometry): THREE.BufferGeometry {
  const index = box.getIndex()!;
  const kept: number[] = [];
  for (const group of box.groups) {
    if (group.materialIndex === 3) continue;
    for (let i = group.start; i < group.start + group.count; i++) kept.push(index.getX(i));
  }
  box.setIndex(kept);
  box.clearGroups();
  const flat = box.toNonIndexed();
  box.dispose();
  return flat;
}

function mergeNonIndexed(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const names = ['position', 'normal', 'color'];
  const out = new THREE.BufferGeometry();
  for (const name of names) {
    const total = parts.reduce((n, g) => n + g.getAttribute(name).array.length, 0);
    const array = new Float32Array(total);
    let at = 0;
    for (const g of parts) {
      array.set(g.getAttribute(name).array as Float32Array, at);
      at += g.getAttribute(name).array.length;
    }
    out.setAttribute(name, new THREE.BufferAttribute(array, 3));
  }
  return out;
}

/**
 * One stack, unit height (the instance scales it), white so the instance colour is the
 * wall's. A cap a little wider than the shaft, because that lip is what makes a box read as
 * a chimney from a hundred metres away.
 */
function plainStack(): THREE.BufferGeometry {
  return boxes([
    { w: 0.62, h: 0.9, d: 0.9, x: 0, y: 0, z: 0, color: 0xffffff },
    { w: 0.78, h: 0.1, d: 1.06, x: 0, y: 0.9, z: 0, color: 0xdddddd },
  ]);
}

/** A Paris stack: a long brick wall of flues with a row of terracotta pots along the top. */
function parisStack(): THREE.BufferGeometry {
  const pots = [-0.6, 0.15, 0.6].map((x) => ({ w: 0.24, h: 0.34, d: 0.24, x, y: 0.82, z: 0, color: 0xc0623e }));
  return boxes([{ w: 2.1, h: 0.82, d: 0.6, x: 0, y: 0, z: 0, color: 0xffffff }, ...pots]);
}

/** A New York water tank: cedar staves on a steel frame, with its conical lid. */
function waterTower(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const c = new THREE.Color();
  const paint = (g: THREE.BufferGeometry, hex: number) => {
    const flat = g.index ? g.toNonIndexed() : g;
    if (flat !== g) g.dispose();
    flat.deleteAttribute('uv');
    c.setHex(hex, THREE.SRGBColorSpace);
    const colors = new Float32Array(flat.getAttribute('position').count * 3);
    for (let i = 0; i < colors.length; i += 3) {
      colors[i] = c.r;
      colors[i + 1] = c.g;
      colors[i + 2] = c.b;
    }
    flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    parts.push(flat);
  };
  const legs = 2.6;
  for (const [x, z] of [[-1.5, -1.5], [1.5, -1.5], [1.5, 1.5], [-1.5, 1.5]]) {
    paint(new THREE.BoxGeometry(0.22, legs, 0.22).translate(x, legs / 2, z), 0x2d2a28);
  }
  paint(new THREE.BoxGeometry(4.0, 0.2, 4.0).translate(0, legs, 0), 0x3a3633);
  paint(new THREE.CylinderGeometry(2.0, 2.15, 3.8, 10, 1, false).translate(0, legs + 0.2 + 1.9, 0), 0x6e4e36);
  paint(new THREE.ConeGeometry(2.2, 1.3, 10).translate(0, legs + 0.2 + 3.8 + 0.65, 0), 0x4a4038);
  const out = mergeNonIndexed(parts);
  for (const g of parts) g.dispose();
  return out;
}
